import { ReactNode, useMemo, useState } from 'react';
import ChevronDownIcon from '../../assets/icons/chevron_down.svg?react';
import {
  Alarm,
  AlarmTriggerState,
  NotificationEvent,
  NotificationMember,
  SortOrder,
  useGetNotificationsQuery,
} from '../../gql/generated';
import { useAlarmFilters } from '../../hooks/useAlarmFilters';
import { cn } from '../../utils/className';
import { formatMetricName } from '../../utils/formatMetric';
import { machineDisplayName } from '../../utils/machineDisplay';
import {
  formatNotificationDate,
  notificationStateLabel,
  notificationStateVariant,
} from '../../utils/notificationFormatters';
import { notificationMembers } from '../../utils/notificationMembers';
import { toOptions, uniqueOptions } from '../../utils/options';
import { Badge } from '../Badge';
import { SelectFilter } from '../tables/columnHelpers';
import { AlarmEventDetail } from './AlarmEventDetail';

interface AlarmEventLogTabProps {
  /** The member alerts of the group, one per signal. */
  alarms: Alarm[];
  /** The signal the log opens narrowed to. */
  presetSignal?: string;
}

const EVENT_LIMIT = 200;

/** One send seen through one of the alarms it covers. */
interface EventLogEntry {
  event: NotificationEvent;
  member: NotificationMember;
}

/**
 * One line of the log: the entries that share a machine, a signal and a state,
 * newest first.
 */
interface EventLogRow {
  key: string;
  machineName: string;
  signal: string;
  state: AlarmTriggerState;
  entries: EventLogEntry[];
}

const machineKey = (event: NotificationEvent) =>
  event.machineId || event.machineName || '';

/** The entry a row stands for: the last alarm it sent. */
const latestEntry = (row: EventLogRow) => row.entries[0];

function buildEventLogRows(entries: EventLogEntry[]): EventLogRow[] {
  const rows = new Map<string, EventLogRow>();

  for (const entry of entries) {
    const { event, member } = entry;
    const signal = member.signal || '';
    const key = `${machineKey(event)}|${signal}|${event.alarmTriggerState}`;
    const existing = rows.get(key);

    if (existing) {
      existing.entries.push(entry);
      continue;
    }

    rows.set(key, {
      key,
      machineName: event.machineName || 'Unknown machine',
      signal,
      state: event.alarmTriggerState,
      entries: [entry],
    });
  }

  for (const row of rows.values()) {
    row.entries.sort((a, b) => b.event.timeUtc.localeCompare(a.event.timeUtc));
  }

  return [...rows.values()].sort((a, b) =>
    latestEntry(b).event.timeUtc.localeCompare(latestEntry(a).event.timeUtc)
  );
}

export function AlarmEventLogTab({
  alarms,
  presetSignal,
}: AlarmEventLogTabProps) {
  const { showAllUsers } = useAlarmFilters();
  const [machineFilter, setMachineFilter] = useState<string[]>([]);
  // The panel unmounts on close, so the preset applies again on every open.
  const [signalFilter, setSignalFilter] = useState<string[]>(
    presetSignal ? [presetSignal] : []
  );
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [selectedEntry, setSelectedEntry] = useState<EventLogEntry | null>(
    null
  );

  // A signal filter narrows the query itself: the log fetches a bounded window
  // of events, and a chatty sibling signal must not push the filtered signal's
  // events out of it. A signal picked off past events may name no live alert;
  // the full list then keeps the query answerable and the client-side filter
  // narrows it.
  const alarmIds = useMemo(() => {
    const narrowed = signalFilter.length
      ? alarms.filter((alarm) => signalFilter.includes(alarm.signal))
      : alarms;
    return (narrowed.length ? narrowed : alarms).map((alarm) => alarm.id);
  }, [alarms, signalFilter]);

  // The machines the alert covers, by the id an event names them with.
  // AlarmMachine.machineId holds it; AlarmMachine.id is the row that joins the
  // machine to one member alarm, so it differs across the members of a group.
  const alertMachineLabels = useMemo(() => {
    const labelByMachineId = new Map<string, string>();
    for (const alarm of alarms) {
      for (const machine of alarm.machines || []) {
        if (machine.machineId && !labelByMachineId.has(machine.machineId)) {
          labelByMachineId.set(machine.machineId, machineDisplayName(machine));
        }
      }
    }
    return labelByMachineId;
  }, [alarms]);

  // A machine filter narrows the query too, for the same reason as the signal
  // filter. An event without a machine id is keyed by name, which the server
  // cannot match, so such a selection stays client-side only.
  const machineIds = useMemo(
    () =>
      machineFilter.length &&
      machineFilter.every((key) => alertMachineLabels.has(key))
        ? machineFilter
        : undefined,
    [machineFilter, alertMachineLabels]
  );

  const { data, loading, error } = useGetNotificationsQuery({
    variables: {
      filter: { alarmIds, machineIds, allUsers: showAllUsers },
      orderBy: { timeUtc: SortOrder.desc },
      pagination: { offset: 0, limit: EVENT_LIMIT },
    },
    skip: alarmIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const events = useMemo(
    () => data?.notifications?.rows ?? [],
    [data?.notifications?.rows]
  );

  // The alert's own machines come first, so the list stays complete while a
  // machine filter narrows the events. Events arrive newest first, so the
  // first event a machine appears in carries its current name.
  const machineOptions = useMemo(() => {
    const labelByKey = new Map(alertMachineLabels);
    for (const event of events) {
      const key = machineKey(event);
      if (!labelByKey.has(key)) {
        labelByKey.set(key, event.machineName || 'Unknown machine');
      }
    }
    return toOptions(
      [...labelByKey.entries()],
      ([key]) => key,
      ([, label]) => label
    );
  }, [alertMachineLabels, events]);

  // Every signal of the alert stays listable, so a preset filter can be lifted
  // while its signal has sent no alarms yet.
  const signalOptions = useMemo(
    () =>
      uniqueOptions([
        ...alarms.map((alarm) => alarm.signal),
        ...events.flatMap((event) =>
          notificationMembers(event).map((member) => member.signal)
        ),
      ]).map((option) => ({
        ...option,
        label: formatMetricName(option.label),
      })),
    [alarms, events]
  );

  const stateOptions = useMemo(
    () =>
      uniqueOptions(events.map((event) => event.alarmTriggerState)).map(
        (option) => ({
          ...option,
          label: notificationStateLabel[option.value as AlarmTriggerState],
        })
      ),
    [events]
  );

  // One send covers every member alarm of an alert group. The log reads per
  // signal, so a send appears once per member it covers.
  // The filters narrow the entries, so a row's count says how many the reader sees
  const rows = useMemo(() => {
    const entries = events.flatMap((event) =>
      notificationMembers(event).map((member) => ({ event, member }))
    );

    const matching = entries.filter(({ event, member }) => {
      if (machineFilter.length && !machineFilter.includes(machineKey(event))) {
        return false;
      }
      if (signalFilter.length && !signalFilter.includes(member.signal || '')) {
        return false;
      }
      return (
        stateFilter.length === 0 ||
        stateFilter.includes(event.alarmTriggerState)
      );
    });

    return buildEventLogRows(matching);
  }, [events, machineFilter, signalFilter, stateFilter]);

  const toggleRow = (key: string) => {
    setExpandedKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  if (selectedEntry) {
    return (
      <AlarmEventDetail
        // The graph inside latches its initial window on mount, so another
        // event remounts the detail view
        key={`${selectedEntry.event.id}:${selectedEntry.member.alarmId ?? ''}`}
        event={selectedEntry.event}
        member={selectedEntry.member}
        onBack={() => setSelectedEntry(null)}
      />
    );
  }

  if (loading && events.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-12'>
        <p className='size-p2 text-tertiary'>Loading event log...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex flex-col items-center justify-center py-12'>
        <p className='size-p2 text-error'>Failed to load the event log</p>
      </div>
    );
  }

  return (
    <div className='overflow-x-auto'>
      <table className='w-full min-w-[520px] border-collapse'>
        <thead>
          <tr
            className='size-p2 text-secondary border-border-secondary
              bg-neutral-050 border-b'
          >
            <th className='w-14' />
            <EventLogHeader label='Machines'>
              <SelectFilter
                options={machineOptions}
                selectedValues={machineFilter}
                onChange={setMachineFilter}
                placeholder='Search machines...'
              />
            </EventLogHeader>
            <EventLogHeader label='Signal'>
              <SelectFilter
                options={signalOptions}
                selectedValues={signalFilter}
                onChange={setSignalFilter}
                placeholder='Search signals...'
              />
            </EventLogHeader>
            <EventLogHeader label='Status'>
              <SelectFilter
                options={stateOptions}
                selectedValues={stateFilter}
                onChange={setStateFilter}
                placeholder='Search statuses...'
              />
            </EventLogHeader>
            <EventLogHeader label='Last alarm sent' />
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => {
            const isExpanded = expandedKeys.has(row.key);
            const isGrouped = row.entries.length > 1;

            return (
              <EventLogGroup
                key={row.key}
                row={row}
                isExpanded={isExpanded}
                isGrouped={isGrouped}
                onToggle={() => toggleRow(row.key)}
                onSelect={setSelectedEntry}
              />
            );
          })}
        </tbody>
      </table>

      {rows.length === 0 && (
        <div
          className='size-p2 text-tertiary flex items-center justify-center px-4
            py-12'
        >
          No alarms sent for this Pulse alert yet.
        </div>
      )}
    </div>
  );
}

interface EventLogHeaderProps {
  label: string;
  children?: ReactNode;
}

function EventLogHeader({ label, children }: EventLogHeaderProps) {
  return (
    <th className='py-3 pr-2 pl-3 text-left font-normal'>
      <div className='flex items-center justify-between gap-1'>
        <span className='truncate'>{label}</span>
        {children}
      </div>
    </th>
  );
}

interface EventLogGroupProps {
  row: EventLogRow;
  isExpanded: boolean;
  isGrouped: boolean;
  onToggle: () => void;
  onSelect: (entry: EventLogEntry) => void;
}

function EventLogGroup({
  row,
  isExpanded,
  isGrouped,
  onToggle,
  onSelect,
}: EventLogGroupProps) {
  return (
    <>
      <tr
        className='border-border-primary/60 hover:bg-surface-focus-support
          cursor-pointer border-b bg-white'
        onClick={() => onSelect(latestEntry(row))}
      >
        <td className='py-3 pl-3'>
          {isGrouped && (
            <button
              type='button'
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${row.machineName} ${formatMetricName(row.signal)}`}
              onClick={(clickEvent) => {
                clickEvent.stopPropagation();
                onToggle();
              }}
              className='flex cursor-pointer items-center gap-2'
            >
              <ChevronDownIcon
                width={12}
                height={12}
                className={cn(
                  'transition-transform',
                  isExpanded && 'rotate-180'
                )}
              />
              <span
                className='bg-surface-focus-support text-focus size-caption flex
                  h-5 w-5 items-center justify-center rounded-full'
              >
                {row.entries.length}
              </span>
            </button>
          )}
        </td>
        <EventLogCells
          machineName={row.machineName}
          signal={row.signal}
          state={row.state}
          sentAt={latestEntry(row).event.timeUtc}
        />
      </tr>

      {isExpanded &&
        row.entries.map((entry) => (
          <tr
            key={entry.event.id}
            className='border-border-primary/60 bg-surface-primary-support
              hover:bg-surface-focus-support cursor-pointer border-b'
            onClick={() => onSelect(entry)}
          >
            <td />
            <EventLogCells
              machineName={entry.event.machineName || 'Unknown machine'}
              signal={entry.member.signal || ''}
              state={entry.event.alarmTriggerState}
              sentAt={entry.event.timeUtc}
            />
          </tr>
        ))}
    </>
  );
}

interface EventLogCellsProps {
  machineName: string;
  signal: string;
  state: AlarmTriggerState;
  sentAt: string;
}

function EventLogCells({
  machineName,
  signal,
  state,
  sentAt,
}: EventLogCellsProps) {
  return (
    <>
      <td className='size-p2 text-primary py-3 pr-2 pl-3'>
        <span className='block truncate' title={machineName}>
          {machineName}
        </span>
      </td>
      <td className='size-p2 text-primary py-3 pr-2 pl-3'>
        <span className='block truncate'>
          {signal ? formatMetricName(signal) : '—'}
        </span>
      </td>
      <td className='py-3 pr-2 pl-3'>
        <Badge variant={notificationStateVariant[state]}>
          {notificationStateLabel[state]}
        </Badge>
      </td>
      <td className='size-p2 text-primary py-3 pr-3 pl-3 whitespace-nowrap'>
        {formatNotificationDate(sentAt)}
      </td>
    </>
  );
}
